import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';

import { environment } from '../../../environments/environment';
import StorageKeys from '../constants/storage';

import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private socket: Socket | null = null;

  constructor(
    private storageService: StorageService
  ) { }

  /**
   * Connect to the presence socket server
   * @param sessionId - The session token from your auth system
   * @param userName - Display name (optional, fallback used if missing)
   */
  connectPresence(sessionId: string, userName: string): void {
    // Check if socket presence is enabled in environment
    if (!environment.SOCKET_PRESENCE?.ENABLED) {
      // console.log('Socket presence is disabled in this environment');
      return;
    }

    // Don't double-connect
    if (this.socket?.connected) {
      // console.log('Socket already connected');
      return;
    }

    const socketServer = environment.SOCKET_PRESENCE.SERVER_URL;

    this.socket = io(socketServer, {
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    this.socket.on('connect', () => {
      // Report this user as online
      this.socket?.emit('user-join', {
        sessionId: sessionId,   // the session token from your auth system
        name: userName,         // display name (optional, fallback used if missing)
        appId: 'scottish-power',      // identifies your app — do not change this
        appVersion: environment.APP_VERSION ?? 'unknown' // your app version for debugging
      });
    });

    this.socket.on('join-error', ({ error }: { error: string }) => {
      // Session is invalid or expired — log the user out
      console.error('Session rejected by socket server:', error);
      this.disconnectPresence();
      // TODO: trigger your app's logout / redirect to login here
    });

    this.socket.on('disconnect', (reason: string) => {
      // console.log('Disconnected from socket server:', reason);
    });

    this.socket.on('connect_error', (err: Error) => {
      // console.error('Socket connection error:', err.message);
    });

    this.socket.on('server-command', ({ requestId, command }: { requestId?: string; command: string }) => {
      const respond = (ok: boolean, data?: unknown, error?: string) => {
        if (!requestId) return; // fire-and-forget — no response needed
        this.socket?.emit('server-command-response', {
          requestId,
          command,
          ok,
          data: data ?? null,
          error: error ?? null,
        });
      };

      if (command === 'hard_refresh') {
        respond(true);
        window.location.reload();
      } else if (command === 'version') {
        respond(true, { version: environment.APP_VERSION ?? 'unknown' });
      } else if (command === 'logout') {
        respond(true);
        // redirect to landing page or trigger logout flow in your app here
        this.storageService.clearSessionStorage();
        this.storageService.clearLocalStorage();
        window.location.href = '/';
      } else {
        respond(false, null, `Unknown command: ${command}`);
      }
    });
  }

  /**
   * Disconnect from the presence socket server
   */
  disconnectPresence(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Get the current socket instance
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  // Call this method to update the user's display name across all sessions
  updateUserName(): void {
    if (this.socket?.connected) {
      const name = this.storageService.getItemFromSessionStorage(StorageKeys.CONTACT_DETAILS)?.firstName;
      const res = this.socket.emit('user-update', {
        sessionId: this.storageService.getSessionId(),
        name: name
      });
    }
  }

  /**
   * Emit a custom event to the socket server
   * @param event - Event name
   * @param data - Event data
   */
  emit(event: string, data?: any): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn('Cannot emit event: socket not connected');
    }
  }

  /**
   * Listen for a custom event from the socket server
   * @param event - Event name
   * @param callback - Callback function to handle the event
   */
  on(event: string, callback: (...args: any[]) => void): void {
    this.socket?.on(event, callback);
  }

  /**
   * Remove a custom event listener
   * @param event - Event name
   * @param callback - Callback function to remove (optional)
   */
  off(event: string, callback?: (...args: any[]) => void): void {
    if (callback) {
      this.socket?.off(event, callback);
    } else {
      this.socket?.off(event);
    }
  }
}
